import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

const TENANT_OVERRIDE_STORAGE_PREFIX = 'worknest-candidate-tenant-override:';
/** Pre-override key kept so existing local setups keep their tenant. */
const LEGACY_LOCAL_DEV_TENANT_STORAGE_KEY = 'worknest-candidate-local-dev-tenant-key';
/** Hosts where the first label *is* the tenant subdomain, whatever it is called. */
const TENANT_HOST_PATTERNS: readonly RegExp[] = [/^([a-z0-9-]+)\.(?:dev\.|uat\.)?careers\.worknest\.cloud$/];
const TENANT_QUERY_KEYS = ['tenant', 'tenantKey', 'organization', 'workspace'];
/**
 * Platform default hostnames (Cloud Run, Azure Static Web Apps, Azure Container Apps). Their first label is a
 * generated service name such as `proud-mud-05e22c50f`, never a tenant.
 */
const PLATFORM_HOST_SUFFIXES: readonly string[] = ['.run.app', '.azurestaticapps.net', '.azurecontainerapps.io'];
const GENERIC_HOST_PARTS = new Set([
  'www',
  'candidate',
  'candidates',
  'jobs',
  'career',
  'careers',
  'dev',
  'uat',
  'prod',
  'worknest',
  'astrecode',
  'cloud',
  'hci',
  'dev-hci',
  'uat-hci',
  'dev-careers',
  'uat-careers',
  'com',
  'net',
  'org',
]);

/**
 * Resolves the tenant the portal talks to, in priority order:
 * 1. a `?tenant=` (or tenantKey / organization / workspace) query parameter — remembered per hostname,
 *    so it survives in-app navigation on any host (localhost, Cloud Run preview URLs, real domains);
 * 2. an override remembered earlier for this hostname;
 * 3. the first non-generic label of the hostname, which is how tenant career portals are addressed:
 *    `acme.careers.worknest.cloud` (prod), `acme.uat.careers.worknest.cloud`, `acme.dev.careers.worknest.cloud`;
 *    environment hosts such as `dev-hci.worknest.cloud` and raw platform URLs (`*.run.app`, `*.azurestaticapps.net`,
 *    `*.azurecontainerapps.io`) carry no tenant;
 * 4. `default`.
 */
@Injectable({ providedIn: 'root' })
export class CandidateTenantContextService {
  private readonly document = inject(DOCUMENT);

  getTenantKey(): string {
    const fromQuery = this.getTenantFromQuery();
    if (fromQuery) {
      this.persistTenantOverride(fromQuery);
      return fromQuery;
    }

    const stored = this.getStoredTenantOverride();
    if (stored) {
      return stored;
    }

    return this.getTenantFromHost() ?? 'default';
  }

  /**
   * Tenant implied by the hostname alone (tenant career subdomain), or null on shared hosts.
   * Job browsing is scoped by this, never by a remembered override, so shared hosts list every company.
   */
  getHostTenantKey(): string | null {
    return this.getTenantFromHost();
  }

  /** Remembers the company a candidate chose (e.g. the job they are applying to) as this host's tenant. */
  setTenantOverride(tenantKey: string | null | undefined): void {
    const normalized = this.normalize(tenantKey);
    if (normalized) this.persistTenantOverride(normalized);
  }

  getTenantQueryParams(): Record<string, string> {
    return {};
  }

  clearTenantSession(): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(this.overrideStorageKey());
    localStorage.removeItem(LEGACY_LOCAL_DEV_TENANT_STORAGE_KEY);
  }

  private getTenantFromQuery(): string | null {
    const search = this.document.defaultView?.location.search ?? '';
    const params = new URLSearchParams(search);
    for (const key of TENANT_QUERY_KEYS) {
      const value = this.normalize(params.get(key));
      if (value) return value;
    }
    return null;
  }

  private getTenantFromHost(): string | null {
    const hostname = this.hostname();
    if (!hostname || this.isLocalHost(hostname) || this.isPlatformHost(hostname)) {
      return null;
    }

    for (const pattern of TENANT_HOST_PATTERNS) {
      const match = hostname.toLowerCase().match(pattern);
      if (match) return this.normalize(match[1]);
    }

    for (const part of hostname.split('.')) {
      const normalized = this.normalize(part);
      if (normalized && !GENERIC_HOST_PARTS.has(normalized)) {
        return normalized;
      }
    }

    return null;
  }

  private hostname(): string {
    return this.document.defaultView?.location.hostname ?? '';
  }

  private isPlatformHost(hostname: string): boolean {
    const host = hostname.toLowerCase();
    return PLATFORM_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
  }

  private isLocalHost(hostname: string): boolean {
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '::1' ||
      /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
    );
  }

  private getStoredTenantOverride(): string | null {
    if (typeof localStorage === 'undefined') return null;
    const scoped = this.normalize(localStorage.getItem(this.overrideStorageKey()));
    if (scoped) return scoped;

    // Older builds only stored an override on localhost, under a host-agnostic key.
    if (this.isLocalHost(this.hostname())) {
      return this.normalize(localStorage.getItem(LEGACY_LOCAL_DEV_TENANT_STORAGE_KEY));
    }
    return null;
  }

  private persistTenantOverride(tenantKey: string): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(this.overrideStorageKey(), tenantKey);
  }

  private overrideStorageKey(): string {
    return TENANT_OVERRIDE_STORAGE_PREFIX + (this.hostname() || 'unknown');
  }

  private normalize(value: string | null | undefined): string | null {
    const normalized = value?.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    return normalized ? normalized : null;
  }
}
